import { CheckCircle, Info, WarningCircle } from 'shared/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '../../ui/Button';
import ErrorCard from '../../ui/ErrorCard';
import { SkeletonTable } from '../../ui/Skeleton';
import StandardTable from '../../ui/StandardTable';
import { GetReconciliation } from './inventoryApi';

/**
 * ⭐ CONCILIAÇÃO SALDO × TRILHA — o DoD do M9, como tela.
 *
 * O contrato diz: "os números do dashboard têm que RECONCILIAR com
 * `wms_stock_movements`". Isso podia ter virado uma frase num relatório de
 * entrega — e envelheceria no dia seguinte. Virou TELA: qualquer um abre e vê,
 * dono por dono e produto por produto, o saldo de agora, a soma dos movimentos
 * e a diferença entre os dois.
 *
 * Zero divergências é a prova. Uma linha divergente é um bug com nome, pallet e
 * produto — não um mal-estar genérico que ninguém sabe por onde começar.
 */

const FormatQty = (value) => new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 6 }).format(Number(value ?? 0));

const InventoryReconciliationPanel = ({ label }) => {
    const [onlyMismatched, setOnlyMismatched] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            setData(await GetReconciliation(onlyMismatched ? { only_mismatched: true } : {}));
        } catch (error) {
            setLoadError(error?.message || 'Erro ao carregar');
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [onlyMismatched]);

    useEffect(() => {
        load();
    }, [load]);

    const columns = useMemo(
        () => [
            {
                key: 'holder_code',
                label: label('wms.reconciliation.holder'),
                getValue: (row) => row.holder_code || '',
                render: (row) => (
                    <span className="font-mono">
                        {row.holder_code || '—'}
                        {row.holder_status === 'CLOSED' ? (
                            <span className="ml-2 text-xs font-sans text-mono-500">
                                {label('wms.lpn_status.CLOSED')}
                            </span>
                        ) : null}
                    </span>
                ),
            },
            { key: 'sku', label: label('wms.field.sku'), getValue: (row) => row.sku || '' },
            {
                key: 'balance_qty',
                label: label('wms.reconciliation.balance'),
                getValue: (row) => Number(row.balance_qty ?? 0),
                render: (row) => FormatQty(row.balance_qty),
            },
            {
                key: 'trail_qty',
                label: label('wms.reconciliation.trail'),
                getValue: (row) => Number(row.trail_qty ?? 0),
                render: (row) => FormatQty(row.trail_qty),
            },
            {
                key: 'difference_qty',
                label: label('wms.reconciliation.difference'),
                getValue: (row) => Number(row.difference_qty ?? 0),
                render: (row) =>
                    row.reconciled ? (
                        <span className="text-mono-500">0</span>
                    ) : (
                        <span className="font-bold text-danger-text dark:text-danger-300">
                            {FormatQty(row.difference_qty)}
                        </span>
                    ),
            },
            {
                key: 'reconciled',
                label: label('wms.common.status'),
                getValue: (row) => (row.reconciled ? 1 : 0),
                render: (row) =>
                    row.reconciled ? (
                        <CheckCircle size={20} weight="fill" className="text-success-text dark:text-success-400" />
                    ) : (
                        <WarningCircle size={20} weight="fill" className="text-danger-text dark:text-danger-400" />
                    ),
            },
        ],
        [label],
    );

    if (loading && !data) {
        return <SkeletonTable columns={6} rows={8} />;
    }

    if (loadError) {
        return (
            <ErrorCard
                title={label('wms.reconciliation.title')}
                message={loadError}
                action={label('wms.common.retry')}
                actionFn={load}
            />
        );
    }

    const summary = data?.summary ?? {};
    const balanced = summary.balanced === true;

    return (
        <div className="space-y-4">
            {/* O VEREDITO primeiro: verde só quando NADA diverge. */}
            <div
                className={`flex items-start gap-3 rounded-xl border-2 px-4 py-4 ${
                    balanced
                        ? 'border-success-400 bg-success-soft dark:border-success-700 dark:bg-success-900/25'
                        : 'border-danger-400 bg-danger-soft dark:border-danger-700 dark:bg-danger-900/25'
                }`}
            >
                {balanced ? (
                    <CheckCircle size={26} weight="fill" className="mt-0.5 shrink-0 text-success-text dark:text-success-300" />
                ) : (
                    <WarningCircle size={26} weight="fill" className="mt-0.5 shrink-0 text-danger-text dark:text-danger-300" />
                )}
                <div className="space-y-1">
                    <p
                        className={`text-base font-bold ${
                            balanced
                                ? 'text-success-text dark:text-success-200'
                                : 'text-danger-text dark:text-danger-200'
                        }`}
                    >
                        {balanced
                            ? label('wms.reconciliation.balanced')
                            : `${summary.mismatched} ${label('wms.reconciliation.mismatched')}`}
                    </p>
                    <p className="text-sm text-mono-600 dark:text-mono-300">
                        {summary.checked} {label('wms.reconciliation.checked').toLowerCase()} ·{' '}
                        {summary.movements_considered} movimentos
                    </p>
                </div>
            </div>

            <div className="flex items-start gap-3 rounded-xl border border-mono-200 px-4 py-3 dark:border-mono-800">
                <Info size={20} className="mt-0.5 shrink-0 text-mono-500" />
                <div className="space-y-1">
                    <p className="text-sm text-mono-600 dark:text-mono-400">{label('wms.reconciliation.hint')}</p>
                    <p className="text-xs text-mono-500 dark:text-mono-500">{label('wms.reconciliation.wip_note')}</p>
                </div>
            </div>

            <StandardTable
                rows={data?.rows ?? []}
                columns={columns}
                rowKey={(row) => `${row.holder_id}-${row.product_id}`}
                countLabel={label('wms.reconciliation.title')}
                exportFileName="wms-conciliacao"
                emptyMessage={label('wms.reconciliation.balanced')}
                toolbarLeft={
                    <Button
                        variant={onlyMismatched ? 'primary' : 'secondary'}
                        size="sm"
                        onClick={() => setOnlyMismatched((value) => !value)}
                    >
                        {label('wms.reconciliation.only_mismatched')}
                    </Button>
                }
            />
        </div>
    );
};

export default InventoryReconciliationPanel;
